/**
 * Signal strength indicator — shows data freshness as a percentage.
 * Calculates: (sources fetched successfully in last 15 min / total sources) x 100.
 */

import { sourceManager } from '@/sources/source-manager';

const FRESHNESS_WINDOW_MS = 15 * 60 * 1000; // 15 minutes
const UPDATE_INTERVAL_MS = 30_000; // update every 30s

let activeInterval: ReturnType<typeof setInterval> | null = null;

export function createSignalIndicator(): HTMLElement {
  // Clear any previous interval (HMR safety)
  if (activeInterval !== null) {
    clearInterval(activeInterval);
    activeInterval = null;
  }

  const indicator = document.createElement('div');
  indicator.className = 'signal-indicator';
  indicator.id = 'signal-indicator';
  indicator.title = 'Data freshness — % of sources with recent data';

  function update(): void {
    const allSources = sourceManager.getAll();
    const total = allSources.length;

    if (total === 0) {
      indicator.textContent = '\uD83D\uDCE1 --';
      indicator.title = 'No sources registered';
      return;
    }

    const cutoff = Date.now() - FRESHNESS_WINDOW_MS;
    let freshCount = 0;

    const statuses = sourceManager.getStatus();
    for (const [, status] of statuses) {
      if (status.lastFetch && status.state === 'active') {
        const fetchTime = new Date(status.lastFetch).getTime();
        if (!Number.isNaN(fetchTime) && fetchTime >= cutoff) {
          freshCount++;
        }
      }
    }

    const pct = Math.round((freshCount / total) * 100);
    indicator.textContent = `\uD83D\uDCE1 ${pct}%`;
    indicator.title = `Signal: ${freshCount}/${total} sources fresh (<15min)`;
  }

  // Initial render
  update();

  // Periodic refresh — store ID for cleanup
  activeInterval = setInterval(update, UPDATE_INTERVAL_MS);

  // Attach destroy for teardown
  (indicator as HTMLElement & { destroy?: () => void }).destroy = () => {
    if (activeInterval !== null) {
      clearInterval(activeInterval);
      activeInterval = null;
    }
  };

  return indicator;
}
